import {
	CheckOutlined,
	CloseOutlined,
	ExclamationCircleOutlined,
	MoneyCollectOutlined,
} from '@ant-design/icons';
import { loanRequestApi } from 'Api/loanRequest';
import { Button, Divider, Modal, Table, Tag, Tooltip } from 'antd';
import Column from 'antd/es/table/Column';
import Title from 'antd/es/typography/Title';
import Buttons from 'components/Global/Buttons';
import Ellipse from 'components/Global/Ellipse';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { bankMapping } from 'utils/mappings';
import {
	Paid,
	PaymentUnderProcessWithText,
	PendingForApproval,
	RejectedByAccounts,
} from 'utils/svgs';
import { decrypt, formatNumber, invalidText, toastText } from 'utils/utils';
import styles from './index.module.scss';
import './index.scss';
type Props = {
	isOpen: boolean;
	handleCancel: () => void;
	handleProceedToPayment: () => void;
	loanIds: string[];
	selectedBankId: string;
	selectedBankData: any;
	fetchLoanRequests: any;
	currency: string;
	conversionRate: number;
};

const encryptionKey: any = process.env.REACT_APP_ENCRYPTION_KEY;
const AddPreviewModal = (props: Props) => {
	const {
		isOpen,
		handleCancel,
		loanIds,
		selectedBankId,
		selectedBankData,
		fetchLoanRequests,
		currency,
		conversionRate,
	} = props;

	const { data } = useSelector((state: any) => state?.userProfile);

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);
	const [employeeData, setEmployeeData] = useState<any[]>([]);

	const handleProceedToPayment = async () => {
		try {
			setIsButtonLoading(true);

			if (!employeeData || employeeData.length === 0) {
				toastText('No employee data found', 'error');
				return;
			}

			let response;

			if (loanIds) {
				const isStanbic = selectedBankData?.bankType === 'STANBIC_BANK';
				response = isStanbic
					? await loanRequestApi.proceedToPaymentForStanbic({
							loanIds: loanIds,
							companyBankId: selectedBankId,
						})
					: await loanRequestApi.proceedToPaymentForFCB({
							loanIds: loanIds,
							companyBankId: selectedBankId,
						});

				fetchEmployeeList(loanIds);
				fetchLoanRequests();
				toastText(
					response.data.message || 'Bank transfer initiated successfully',
					'success',
				);
			} else {
				toastText('Something went wrong in getting loanId', 'error');
			}
		} catch (err: any) {
			const errorMessage =
				err?.response?.data?.message || 'Something went wrong in bank transfer';
			toastText(errorMessage, 'error');
		} finally {
			setIsButtonLoading(false);
		}
	};

	const fetchEmployeeList = async (id: string[]) => {
		try {
			setIsLoading(true);
			const response = await loanRequestApi.getEmployeeDetailsForLoan(id);

			const employeeObject = response.data.data;

			if (
				employeeObject &&
				typeof employeeObject === 'object' &&
				!Array.isArray(employeeObject)
			) {
				setEmployeeData([employeeObject]);
			} else if (Array.isArray(employeeObject)) {
				setEmployeeData(employeeObject);
			} else {
				setEmployeeData([]);
			}
		} catch (err: any) {
			const errorMessage =
				err?.response?.data?.message ||
				'Something went wrong in fetching employee details';
			toastText(errorMessage, 'error');
			setEmployeeData([]);
		} finally {
			setIsLoading(false);
		}
	};
	useEffect(() => {
		if (loanIds && isOpen) {
			fetchEmployeeList(loanIds);
		}
	}, [loanIds, isOpen]);

	const shouldDisableProceedButton =
		isLoading ||
		!employeeData ||
		employeeData.length === 0 ||
		(employeeData[0] &&
			(employeeData[0].status === 'SUCCESS' ||
				employeeData[0].status === 'PROCESSING'));

	const myButtons = [
		{
			text: 'Back',
			isLoading: false,
			className: 'btn-cancel',
			isSubmit: false,
			disabled: isLoading,
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
			onclick: () => {
				handleCancel();
			},
		},
		...(!shouldDisableProceedButton
			? [
					{
						text: 'Proceed to Payment',
						isLoading: isButtonLoading,
						className: 'btn-blue',
						isSubmit: false,
						fontSize: '1.8rem',
						minWidth: '12rem',
						minHeight: '4rem',
						disabled: isLoading,
						onclick: () => {
							handleProceedToPayment();
						},
						font: '16px',
					},
				]
			: []),
	];

	return (
		<>
			<Modal
				open={isOpen}
				closable={false}
				maskClosable={false}
				onCancel={handleCancel}
				rootClassName="addPaymentModalRoot"
				style={{
					minWidth: '1300px',
				}}
				footer={false}
			>
				<div className={`${styles['modal']} modal`}>
					<div className={styles['modal-header']}>
						<Title level={4}>
							<span>
								Review the payment details for{' '}
								<span style={{ color: '#584495' }}>
									{employeeData && employeeData.length > 0
										? `${employeeData.length} ${
												employeeData.length > 1 ? 'employees' : 'employee'
											}`
										: 'employees'}
								</span>{' '}
								and click Proceed.
							</span>
						</Title>
						<div
							style={{
								display: 'flex',
								justifyContent: 'flex-end',
								alignItems: 'center',
								gap: '20px',
							}}
						>
							<div>
								<Button icon={<CloseOutlined />} onClick={handleCancel} />
							</div>
						</div>
					</div>
					<Divider
						style={{
							margin: '12px 0 ',
							padding: '0',
						}}
					/>
					<div className={styles['company-account-details']}>
						<p className={styles['details-label']}>Company Account Details</p>
						<br />
					</div>
					<div className={styles['company-account-details']}>
						<div className={styles['detail-group']}>
							<div className={styles['detail-label']}>Account Name</div>
							<div className={styles['detail-value']}>
								{selectedBankData?.accountName}
							</div>
						</div>

						<div className={styles['detail-group']}>
							<div className={styles['detail-label']}>Bank Type</div>
							<div className={styles['detail-value']}>
								{bankMapping[selectedBankData?.bankType]?.name}
							</div>
						</div>

						{selectedBankData?.accountNumber && (
							<div className={styles['detail-group']}>
								<div className={styles['detail-label']}>Account Number</div>
								<div className={styles['detail-value']}>
									{decrypt(selectedBankData?.accountNumber, encryptionKey)}
								</div>
							</div>
						)}
						{currency === 'USD' && (
							<div className={styles['detail-group']}>
								<div className={styles['detail-label']}>Conversion Rate</div>
								<div className={styles['detail-value']}>{conversionRate}</div>
							</div>
						)}
					</div>
					<div className={styles['modal-body']} style={{ marginBottom: 0 }}>
						<div className="payment-table">
							<Table
								dataSource={employeeData}
								scroll={{
									x: 1500,
								}}
								rowKey={(record: any) => record.id}
								pagination={false}
								style={{
									marginTop: 2,
								}}
								loading={isLoading}
							>
								<Column
									title="Employee Name"
									dataIndex="employeeName"
									key="employeeName"
									className="bg-white"
									width={130}
									fixed="left"
									render={(text: string, record: any) => {
										const {
											beneficiaryAddr1,
											beneficiaryAddr2,
											beneficiaryAddr3,
											employeeCode,
										} = record;

										const address = [
											beneficiaryAddr1,
											beneficiaryAddr2,
											beneficiaryAddr3,
										]
											.filter(Boolean)
											.join(' ');

										const hasSpecialChar = /[^a-zA-Z0-9\s]/.test(address);

										return (
											<span>
												{text}
												<span
													style={{
														color: '#584495',
														fontWeight: 500,
													}}
												>
													{' '}
													| {employeeCode}
												</span>
												<Tooltip title="Address contains special characters">
													{hasSpecialChar && (
														<ExclamationCircleOutlined
															style={{
																color: '#faad14',
																marginLeft: 6,
															}}
														/>
													)}
												</Tooltip>
											</span>
										);
									}}
								/>

								<Column
									title="Bank"
									dataIndex="bank"
									key="bank"
									className="bg-white"
									width={80}
									render={(value: string) => {
										return bankMapping[value]?.name ?? '';
									}}
								/>
								<Column
									title="Bank Account Number"
									key="accountDetails"
									className="bg-white"
									width={150}
									render={(_, record: any) => {
										const accountNumber = !invalidText(record.accountNumber)
											? decrypt(record.accountNumber, encryptionKey)
											: '';
										if (!accountNumber) {
											return <span>-</span>;
										}

										const isVerified =
											record.bank === 'STANBIC_BANK' || record.accountVerified;

										return (
											<Tooltip
												title={
													isVerified
														? 'Account number is verified'
														: 'Account number is not verified'
												}
											>
												<div
													style={{
														display: 'flex',
														alignItems: 'center',
														gap: 8,
													}}
												>
													<span>{accountNumber}</span>
													{isVerified ? (
														<CheckOutlined
															style={{
																color: 'green',
															}}
														/>
													) : (
														<CloseOutlined
															style={{
																color: 'red',
															}}
														/>
													)}
												</div>
											</Tooltip>
										);
									}}
								/>
								<Column
									title="Sort Code"
									dataIndex="sortCode"
									key="sortCode"
									className="bg-white"
									width={75}
								/>
								<Column
									title="Amount"
									dataIndex="amount"
									key="amount"
									className="bg-white"
									width={80}
									render={(value: string, record: any) => {
										return formatNumber(
											Number(value),
											data?.CompanyCurrencies,
											currency === 'USD' ? true : false,
										);
									}}
								/>
								{currency === 'USD' && (
									<Column
										title="Final Amount (ZMW)"
										dataIndex="convertedAmount"
										key="convertedAmount"
										className="bg-white"
										width={120}
										render={(value: string) => {
											return formatNumber(
												Number(value),
												data?.CompanyCurrencies,
												false,
											);
										}}
									/>
								)}
								<Column
									title="Beneficiary Address"
									key="beneficiaryAddress"
									width={'15%'}
									className="bg-white"
									render={(_, record) => {
										const {
											beneficiaryAddr1,
											beneficiaryAddr2,
											beneficiaryAddr3,
										}: any = record;

										const addressParts = [
											beneficiaryAddr1,
											beneficiaryAddr2,
											beneficiaryAddr3,
										].filter(Boolean);

										const address = addressParts.join(' ') || '-';

										return (
											<Ellipse
												message={address}
												maxLength={50}
												tooltipMessage={address}
												isTooltip={true}
											/>
										);
									}}
								/>
								<Column
									title="Status"
									dataIndex="status"
									key="status"
									width={100}
									className={styles['statusColumn'] + ' bg-white'}
									render={(value: string, record: any) => {
										let statusText = '';
										let statusClass = '';
										let StatusIcon = null;

										const currentStatus = value;

										switch (currentStatus) {
											case 'PENDING':
												statusText = 'Pending';
												statusClass = styles['status-pending'];
												StatusIcon = <PendingForApproval />;
												break;
											case 'FAILED':
												statusText = 'Failed';
												statusClass = styles['status-rejected'];
												StatusIcon = <RejectedByAccounts />;
												break;
											case 'SUCCESS':
												statusText = 'Success';
												statusClass = styles['status-success'];
												StatusIcon = <Paid />;
												break;
											case 'PROCESSING':
												statusText = '';
												statusClass = styles['status-processing'];
												StatusIcon = <PaymentUnderProcessWithText />;

												break;
											case 'DONE_VIA_CASH':
												statusText = '';
												statusClass = styles['status-success'];
												StatusIcon = <MoneyCollectOutlined />;

												break;
											default:
												statusText = 'Unknown Status';
												statusClass = styles['status-unknown'];
												StatusIcon = null;
										}

										return (
											<div className={styles['statusContainer']}>
												<div className={styles['statusDisplay']}>
													<div className={styles['statusAndEdit']}>
														<Tag
															className={`${styles['statusTag']} ${statusClass}`}
															bordered={false}
														>
															<span className={styles['statusIcon']}>
																{StatusIcon}
															</span>
															<span>{statusText}</span>
														</Tag>
													</div>
												</div>
											</div>
										);
									}}
								/>
							</Table>
						</div>
					</div>
				</div>
				<div>
					<Buttons buttons={myButtons} />
				</div>
			</Modal>
		</>
	);
};

export default AddPreviewModal;
